/**
 * HUG - Artist Management Platform
 * Copyright (c) 2025 Underplay Music Ltd. All rights reserved.
 * 
 * This software and associated documentation files are the proprietary 
 * property of Underplay Music Ltd and are protected by copyright law.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { TrendingUp, TrendingDown, DollarSign, Receipt, Users, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinanceOverview = ({ user, setSelectedArtist }) => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchOverview();
    }
  }, [user]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/finance/all-artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(response.data);
    } catch (error) {
      toast.error('Failed to fetch finance overview');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `£${amount.toFixed(2)}`;
  };

  const handleViewArtist = (artistId) => {
    setSelectedArtist(artistId);
    navigate('/finance');
  };

  // Both managers and admins can view finance overview
  if (user?.role !== 'manager' && user?.role !== 'admin') {
    return (
      <div className="space-y-8">
        <Card className="bg-card border-border/50 p-12 text-center">
          <p className="text-muted-foreground">Only managers and admins can view the finance overview.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <Card className="bg-card border-border/50 p-12 text-center">
          <p className="text-muted-foreground">Loading finance overview...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-2xl text-foreground">Expenses Overview</h1>
          <p className="text-muted-foreground text-sm mt-1">Summary of all artists&apos; financial performance</p>
        </div>
      </div>

      {/* Grand Totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Artists</p>
              <p className="font-manrope font-bold text-xl text-foreground">{overview?.artists?.length || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Income</p>
              <p className="font-manrope font-bold text-xl text-foreground">{formatCurrency(overview?.totals?.income || 0)}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Costs</p>
              <p className="font-manrope font-bold text-xl text-foreground">{formatCurrency(overview?.totals?.costs || 0)}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              (overview?.totals?.net_profit || 0) >= 0 ? 'bg-blue-500/20' : 'bg-orange-500/20'
            }`}>
              <DollarSign className={`w-5 h-5 ${
                (overview?.totals?.net_profit || 0) >= 0 ? 'text-blue-500' : 'text-orange-500'
              }`} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Net Profit</p>
              <p className={`font-manrope font-bold text-xl ${
                (overview?.totals?.net_profit || 0) >= 0 ? 'text-foreground' : 'text-orange-500'
              }`}>
                {formatCurrency(overview?.totals?.net_profit || 0)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Commission Owed</p>
              <p className="font-manrope font-bold text-xl text-foreground">{formatCurrency(overview?.totals?.commission_owed || 0)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Artist Breakdown */}
      <div>
        <h3 className="font-semibold text-lg mb-3">Artist Breakdown</h3>
        <div className="grid gap-3">
          {overview?.artists && overview.artists.length > 0 ? (
            overview.artists.map((artist) => (
              <Card key={artist.artist_id} className="bg-card border-border/50 p-4 hover:border-primary/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg mb-2">{artist.artist_name}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Income</p>
                        <p className="font-semibold text-green-500">{formatCurrency(artist.total_income)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Costs</p>
                        <p className="font-semibold text-red-500">{formatCurrency(artist.total_costs)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Net Profit</p>
                        <p className={`font-semibold ${
                          artist.net_profit >= 0 ? 'text-blue-500' : 'text-orange-500'
                        }`}>
                          {formatCurrency(artist.net_profit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Commission Owed</p>
                        <p className="font-semibold text-primary">{formatCurrency(artist.commission_owed)}</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewArtist(artist.artist_id)}
                    className="ml-4"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </Card>
            ))
          ) : (
            <Card className="bg-card border-border/50 p-12 text-center">
              <p className="text-muted-foreground">No artist data available yet.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceOverview;
